import { Hole, HoleStatus, HoleStatusHistoryUtil } from 'src/app/services/hole.service';

// ─────────────────────────────────────────────────────────────────────────────
// Hole status helpers shared by the Holes tab views (pure functions, no Angular)
// ─────────────────────────────────────────────────────────────────────────────

/** Icon used for the "Actual" legend entry (every status). */
export const ALL_HOLES_ICON = 'assets/icon/all_holes.svg';

/** Same mapping as HoleLayer.getHoleIconPath, exposed for legends and tables. */
export const HOLE_STATUS_ICONS: Readonly<Record<HoleStatus, string>> = {
  [HoleStatus.UNSET]:      'assets/icon/hole-gray.svg',
  [HoleStatus.PLANNED]:    'assets/icon/hole-planned.svg',
  [HoleStatus.DRILLED]:    'assets/icon/hole-drilled.svg',
  [HoleStatus.TO_REDRILL]: 'assets/icon/hole-to-redrill.svg',
  [HoleStatus.REDRILLED]:  'assets/icon/hole-redrilled.svg',
  [HoleStatus.TOADJUST]:   'assets/icon/hole-to-adjust.svg',
  [HoleStatus.MISSING]:    'assets/icon/hole-missing.svg',
  [HoleStatus.COLLAPSED]:  'assets/icon/hole-collapsed.svg',
  [HoleStatus.LOADING]:    'assets/icon/hole-loading.svg',
  [HoleStatus.LOADED]:     'assets/icon/hole-loaded.svg',
  [HoleStatus.BLASTED]:    'assets/icon/hole-blasted.svg',
};

export function holeStatusIcon(status: string | null | undefined): string {
  return HOLE_STATUS_ICONS[status as HoleStatus] ?? HOLE_STATUS_ICONS[HoleStatus.UNSET];
}

/** Legend order of the State view (Figma 2544:670271). */
export const STATE_LEGEND_STATUSES: readonly HoleStatus[] = [
  HoleStatus.MISSING,
  HoleStatus.PLANNED,
  HoleStatus.DRILLED,
  HoleStatus.LOADED,
  HoleStatus.LOADING,
  HoleStatus.TOADJUST,
  HoleStatus.COLLAPSED,
  HoleStatus.TO_REDRILL,
  HoleStatus.REDRILLED,
  HoleStatus.BLASTED,
];

/** A hole counts as drilled once it reached any of these statuses (same set as the legacy drilling chart). */
export const DRILLED_STATUSES: ReadonlySet<HoleStatus> = new Set<HoleStatus>([
  HoleStatus.DRILLED,
  HoleStatus.TO_REDRILL,
  HoleStatus.REDRILLED,
  HoleStatus.TOADJUST,
  HoleStatus.LOADING,
  HoleStatus.LOADED,
  HoleStatus.BLASTED,
]);

/** A hole counts as loaded once it reached any of these statuses. */
export const LOADED_STATUSES: ReadonlySet<HoleStatus> = new Set<HoleStatus>([
  HoleStatus.LOADING,
  HoleStatus.LOADED,
  HoleStatus.BLASTED,
]);

/** True when the hole has ever been in one of `statuses` (status history first, current status as fallback). */
export function hasReachedAny(hole: Pick<Hole, 'Status' | 'statusHistory'>, statuses: ReadonlySet<HoleStatus>): boolean {
  if (hole.statusHistory !== undefined && hole.statusHistory !== null) {
    return HoleStatusHistoryUtil.hasHadAnyStatus(hole.statusHistory, statuses as Set<HoleStatus>);
  }
  return statuses.has(hole.Status as HoleStatus);
}

export interface HoleProgress {
  count:   number;
  total:   number;
  percent: number;
}

export function holeProgress(holes: readonly Pick<Hole, 'Status' | 'statusHistory'>[], statuses: ReadonlySet<HoleStatus>): HoleProgress {
  const total = holes.length;
  const count = holes.filter(hole => hasReachedAny(hole, statuses)).length;
  return { count, total, percent: total ? Math.round((count / total) * 100) : 0 };
}

/** Number of holes currently in each status. */
export function countByStatus(holes: readonly Pick<Hole, 'Status'>[]): Map<HoleStatus, number> {
  const counts = new Map<HoleStatus, number>();
  for (const hole of holes) {
    const status = hole.Status as HoleStatus;
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  return counts;
}
